import type { Payment } from "@domain/models/Payment";
import { Model, GQL, Utils } from "@binale-tech/shared";
import type { GenericRecord } from "@domain/models/GenericRecord";
import { type GenericRecordTableItem } from "@domain/models/AccountingTableTypes";
import { PaymentUtils } from "@domain/models/utils/PaymentUtils";
import type { Product, RecordViewCapabilities } from "@domain/core/Product";
import { logger } from "@domain/infrastructure/logger";
import type { RecordFormState } from "@domain/models/RecordFormState";
import { type IGenericItem, type IGenericRecord } from "@domain/models/Interfaces";
import { ProductAccessUtils } from "@domain/models/utils/ProductAccessUtils";
import { RecordDraftValidator } from "@domain/models/utils/RecordDraftValidator";
import { getRecordId } from "@accounting/shared/TableAccounting/components/tools";

interface ManipulationPermissionsSection {
    disabled: boolean;
    reason?: string;
}

export interface ManipulationPermissions {
    remove: ManipulationPermissionsSection;
    cancel: ManipulationPermissionsSection;
    edit: ManipulationPermissionsSection;
    confirm: ManipulationPermissionsSection;
    copy: ManipulationPermissionsSection;
    paymentSave: ManipulationPermissionsSection;
    paymentDelete: ManipulationPermissionsSection;
    /** Attachment editing - follows record content mutability only, never the view's form capability (FIBU-612). */
    documents: ManipulationPermissionsSection;
    /** Zahlungsavis toggle - record state + the view's avis capability, decoupled from the record form. */
    avis: ManipulationPermissionsSection;
}

/** Permissions that depend only on record state + authz, before per-view capabilities are applied. */
type RecordStatePermissions = Omit<ManipulationPermissions, "documents" | "avis">;

export class GenericRecordUtils {
    protected static readonly ResEnabled: RecordStatePermissions = {
        remove: { disabled: false },
        cancel: { disabled: false },
        edit: { disabled: false },
        copy: { disabled: false },
        confirm: { disabled: false },
        paymentSave: { disabled: false },
        paymentDelete: { disabled: false },
    };

    static readonly OVERVIEW_CAP_REASON = "This record can only be edited in its main module";

    static readonly SALDO_ORIGIN_REASON =
        "This record is carried forward by a Saldenvortrag, it can't be deleted or cancelled";

    /**
     * Full manipulation permissions = record-state permissions (authz + journaled/Storno/reviewed/
     * year/period/draft) intersected with the view's capabilities (which record-manipulation surfaces
     * the current view exposes). Keeping the intersection here makes the domain layer the single
     * source of truth - action surfaces read the resulting flags and never re-check view config.
     */
    static getManipulationPermissions(
        record: GenericRecord,
        hasProgramWritePermission: boolean,
        programProduct: Product,
        paymentsData: Map<string, Payment[]>,
        editBounds: { selectedPeriod: number; globalYear: number },
        yearConfig: GQL.ICompanyAccountingYear,
        companyGQL: GQL.ICompany,
        user: Model.UserInterface,
        saldoSourceIds: ReadonlySet<string>,
    ): ManipulationPermissions {
        const state = this.getRecordStatePermissions(
            record,
            hasProgramWritePermission,
            programProduct,
            paymentsData,
            editBounds,
            yearConfig,
            companyGQL,
            user,
            saldoSourceIds,
        );
        return this.applyViewCapabilities(state, programProduct);
    }

    private static defaultViewCapabilities(enableRecordForm: boolean): RecordViewCapabilities {
        if (enableRecordForm) {
            return { openForm: true, copy: true, delete: true, cancel: true, avis: true };
        }
        // Overview default (per-product Fälligkeit/OPOS/AVIS views): the record form and copy are
        // unavailable, but delete, Storno and Avis stay (product decision 2026-07). The common
        // cross-product overviews (KontenAuskunft, CommonAuswertung) additionally turn delete and
        // Storno off via ProductAggregation's recordViewCapabilities override (decision 2026-07-22).
        // documents + payments are never capped here - they follow record state (see applyViewCapabilities).
        return { openForm: false, copy: false, delete: true, cancel: true, avis: true };
    }

    private static getViewCapabilities(product: Product | null | undefined): RecordViewCapabilities {
        const config = product?.getConfig();
        const base = this.defaultViewCapabilities(config?.enableRecordForm ?? false);
        return config?.recordViewCapabilities ? { ...base, ...config.recordViewCapabilities } : base;
    }

    private static applyViewCapabilities(state: RecordStatePermissions, product: Product): ManipulationPermissions {
        const caps = this.getViewCapabilities(product);
        const res: ManipulationPermissions = {
            remove: { ...state.remove },
            cancel: { ...state.cancel },
            edit: { ...state.edit },
            copy: { ...state.copy },
            confirm: { ...state.confirm },
            paymentSave: { ...state.paymentSave },
            paymentDelete: { ...state.paymentDelete },
            // Attachment editing tracks record content mutability (record-state edit), never the view's
            // form capability - this is the FIBU-612 fix that keeps documents editable in overviews.
            documents: { disabled: state.edit.disabled, reason: state.edit.reason },
            // Avis baseline mirrors record state; the view's avis capability is applied below.
            avis: { disabled: state.edit.disabled, reason: state.edit.reason },
        };
        const capOff = (section: ManipulationPermissionsSection, allowed: boolean) => {
            if (!allowed && !section.disabled) {
                section.disabled = true;
                section.reason = this.OVERVIEW_CAP_REASON;
            }
        };
        capOff(res.edit, caps.openForm);
        capOff(res.copy, caps.copy);
        capOff(res.remove, caps.delete);
        capOff(res.cancel, caps.cancel);
        capOff(res.avis, caps.avis);
        return res;
    }

    private static getRecordStatePermissions(
        record: GenericRecord,
        hasProgramWritePermission: boolean,
        programProduct: Product,
        paymentsData: Map<string, Payment[]>,
        editBounds: { selectedPeriod: number; globalYear: number },
        yearConfig: GQL.ICompanyAccountingYear,
        companyGQL: GQL.ICompany,
        user: Model.UserInterface,
        saldoSourceIds: ReadonlySet<string>,
    ): RecordStatePermissions {
        const { selectedPeriod: selectedPeriodEditBound, globalYear } = editBounds;
        const res: RecordStatePermissions = {
            remove: { disabled: true },
            cancel: { disabled: true },
            edit: { disabled: true },
            copy: { disabled: true },
            confirm: { disabled: true },
            paymentDelete: { disabled: true, reason: "defaultState" },
            paymentSave: { disabled: true, reason: "defaultState" },
        };
        if (!programProduct) {
            return res;
        }
        const setRecordAllDisabledWithReason = (v: string) => {
            res.remove.disabled = true;
            res.remove.reason = v;
            res.edit.disabled = true;
            res.edit.reason = v;
            res.copy.disabled = true;
            res.copy.reason = v;
            res.confirm.disabled = true;
            res.confirm.reason = v;
            res.cancel.disabled = true;
            res.cancel.reason = v;
        };
        const enableConfirmIfAllowed = () => {
            if (record.draft) {
                const isAllowed = this.isConfirmationAllowed(record, programProduct);
                if (isAllowed) {
                    res.confirm = { disabled: false };
                } else {
                    res.confirm = { disabled: true, reason: "Record is invalid" };
                }
            }
        };
        const setPaymentWithReason = (opts: { disableDelete?: boolean; disableSave?: boolean }, reason?: string) => {
            res.paymentDelete.disabled = opts.disableDelete;
            if (opts.disableDelete) {
                res.paymentDelete.reason = reason;
            }

            res.paymentSave.disabled = opts.disableSave;
            if (opts.disableSave) {
                res.paymentSave.reason = reason;
            }
        };

        const enableRecordEdit = (v: string) => {
            res.edit.disabled = res.remove.disabled = res.cancel.disabled = res.copy.disabled = false;
            res.confirm.reason = v;
            res.confirm.reason = v;
        };
        if (!hasProgramWritePermission) {
            // disable payment
            const reason = "You do not have write permission";
            setRecordAllDisabledWithReason(reason);
            setPaymentWithReason({ disableDelete: true, disableSave: true }, reason);
            return res;
        }
        if (!record.key) {
            return this.ResEnabled;
        }
        const locks = new Set(Utils.RecordLock.getRecordContentLockReasons(record));
        if (locks.has("journaled")) {
            // enable payment save
            // enable payment delete
            setRecordAllDisabledWithReason("Record is in journal");
            if (!record.cancellation) {
                res.cancel = { disabled: false };
            }
            res.copy = { disabled: false };
            setPaymentWithReason({ disableDelete: false, disableSave: false });
            return res;
        }
        if (locks.has("cancellation")) {
            // disable payment save
            // enable  payment delete
            const reason =
                record.cancellation === "cancelled"
                    ? "Record is cancelled (Storno)"
                    : "Record is cancellation counterweight";
            setRecordAllDisabledWithReason(reason);
            if (record.cancellation === "counterweight") {
                res.remove = { disabled: false };
            } else {
                res.copy = { disabled: false };
            }
            setPaymentWithReason({ disableDelete: false, disableSave: true }, reason);
            return res;
        }
        if (locks.has("reviewed") && !ProductAccessUtils.canReviewRecord(record.getProductKey(), companyGQL, user)) {
            const reason = "This record was reviewed, no changes are allowed";
            setRecordAllDisabledWithReason(reason);
            res.copy = { disabled: false };
            setPaymentWithReason({ disableDelete: false, disableSave: false }, reason);
            return res;
        }
        if (record.date.getFullYear() !== globalYear) {
            // enable payment save
            // enable payment delete
            setRecordAllDisabledWithReason(
                "Selected global year doesn't match record (or original saldo source record) year",
            );
            res.copy = { disabled: false };
            setPaymentWithReason({ disableDelete: false, disableSave: false });
            return res;
        }
        if (Number.isFinite(selectedPeriodEditBound)) {
            if (record.period !== selectedPeriodEditBound) {
                setRecordAllDisabledWithReason("Selected period doesn't match record's period");
                res.copy = { disabled: false };
                setPaymentWithReason({ disableDelete: false, disableSave: false });
                // enable payment save
                // enable payment delete
                return res;
            }
        }
        const pk = record.getProductKey();
        if (!pk) {
            logger.log("record without product key", JSON.stringify(this));
            throw new Error("record without product key");
        }

        if (PaymentUtils.isFERecordAutoBlocked(record, paymentsData)) {
            setRecordAllDisabledWithReason(`This is auto record, it's not editable. Please edit original payment`);
            return res;
        }
        if (locks.has("saldoTransfer")) {
            res.remove = { disabled: false };
            res.copy = { disabled: false };
            enableConfirmIfAllowed();
            res.edit.reason = `This is auto saldo transfer record, it's not editable`;
            setPaymentWithReason({ disableDelete: false, disableSave: false });
            return res;
        }
        if (PaymentUtils.isRepresentationRecordEditBlocked(record, paymentsData, yearConfig)) {
            res.remove.disabled = false;
            enableConfirmIfAllowed();
            res.edit.reason = `This is connected split representation IST record, it's not editable`;
            setPaymentWithReason({ disableDelete: false, disableSave: false });
            return res;
        }
        if (record.draft) {
            res.edit.disabled = res.remove.disabled = res.copy.disabled = false;
            res.edit.reason = res.remove.reason = res.copy.reason = "Record is draft";
            enableConfirmIfAllowed();
            setPaymentWithReason({ disableDelete: false, disableSave: false });
            return res;
        }
        // local updates
        if (!record.draft) {
            enableRecordEdit(`Already confirmed`);
            setPaymentWithReason({ disableDelete: false, disableSave: false });
            // if saldo origin restrict removal and storno (would fail in the backend because of FK constraint)
            if (saldoSourceIds.has(getRecordId(record.key))) {
                res.remove = { disabled: true, reason: this.SALDO_ORIGIN_REASON };
                res.cancel = { disabled: true, reason: this.SALDO_ORIGIN_REASON };
            }
            return res;
        }

        return res;
    }

    static isRecordPayed(record: GenericRecord, payments: Map<GenericRecord, Payment[]>) {
        return payments && record.getOpenBrutto(payments.get(record)) === 0;
    }

    static copyRecord(proto: GenericRecord, year: number, period?: number): GenericRecord {
        const record = proto.clone();
        let recordMonth = record.date.getMonth();
        let recordPeriod = record.period;
        let recordDay = record.date.getDate();
        if (Number.isFinite(period)) {
            recordPeriod = period;
            const { month, day } = Model.getMonthAndDay(period);
            recordMonth = month;
            if (day) {
                recordDay = day;
                // in case date was 31 and new month has less days
            } else if (new Date(year, recordMonth, recordDay).getMonth() !== recordMonth) {
                recordDay = new Date(year, recordMonth + 1, 0).getDate();
            }
        }
        record.date.setFullYear(year, recordMonth, recordDay);
        record.period = recordPeriod;
        record.day = recordDay;
        record.year = year;

        record.key = null;
        record.documents = null;
        record.falligkeit = null;
        record.review = undefined;
        Object.assign(record, { journaled: false, saldoSourceId: undefined });
        if (record.priority) {
            record.priority++;
        }
        return record;
    }

    static isAssetsAndColorDisabled(tableItem: GenericRecordTableItem) {
        return Boolean(tableItem.extra?.isVirtualRecord);
    }

    static mapToGQL(record: GenericRecord): GQL.IGenericRecordInput {
        delete (record as any).removedAt;
        delete (record as any).removed;
        delete (record as any).groupId;
        // legacy key, still present on RTDB nodes written before the notes thread migration
        delete (record as any).notes;
        delete (record as any).ustvaExportIds;
        delete record.aiReview;
        delete record.recordOrigin;
        const {
            debetor,
            creditor,
            category,
            updatedAt,
            updatedBy,
            color,
            review,
            brutto,
            date,
            priority,
            journaled,
            cancellation,
            saldoSourceId,
            avis,
            currency,
            notesColor,
            ...restRecord
        } = record;
        const currencyData = Object.keys(currency ?? {}).length ? currency : undefined;
        const contactId = restRecord.contactId;

        return {
            ...restRecord,
            contactId,
            documents: (restRecord.documents || []).map(({ id }) => ({ id })),
            currency: currencyData,
            originalAmount: currencyData ? Number(restRecord.originalAmount) : undefined,
            year: record.date.getFullYear(),
            day: record.date.getDate(),
            falligkeit: record.falligkeit ? record.falligkeit.toISOString() : undefined,
            num: record.num,
            period: record.period,
            category: category?.num,
            creditor: creditor?.num,
            debitor: debetor?.num,
            items: record.items.map((item) => {
                const { debetor: debItem, ...rest } = item;
                return {
                    ...rest,
                    originalAmount: currency ? Number(item.originalAmount) : undefined,
                    category: item.category?.num,
                    creditor: item.creditor?.num,
                    debitor: debItem?.num,
                    tag: item.tag?.num,
                    bu: item.bu || 0,
                    USt13b: item.USt13b ? Number(item.USt13b) : undefined,
                };
            }),
        };
    }

    static getInvoiceNumber = (r: GenericRecord, pk: GQL.IProductKey, useDivRepresentation?: boolean) => {
        if (Utils.ModuleUtils.useBelegfeld1Split(pk)) {
            if (useDivRepresentation) {
                return r.getBelegfeld1();
            }
            return r.items.find((el) => el.belegfeld1)?.belegfeld1;
        }

        return r.num;
    };

    static getOpenBrutto(brutto: number, payments: Payment[] = []) {
        let openBrutto = brutto;
        if (payments) {
            let paid = 0;
            payments.forEach((payment) => {
                if (!payment) {
                    logger.logToSentry("empty payment", payments);
                } else {
                    paid += payment.getSum();
                }
            });

            openBrutto -= paid;
        }
        return Object.is(-0, openBrutto) ? 0 : openBrutto;
    }

    protected static isConfirmationAllowed(item: GenericRecord, product: Product): boolean {
        if (!item.draft) {
            return false;
        }
        const validator = new RecordDraftValidator(product.getConfig());
        return validator.canConfirmRecord(item);
    }

    static convertRecordToForm = (r: IGenericRecord): RecordFormState["editableRecord"] => {
        return {
            recordKey: r.key,
            recordNum: r.num,
            recordBrutto: r.brutto,
            recordCurrency: r.currency,
            recordOriginalAmount: r.originalAmount,
            recordCategoryCreditor: r.creditor || r.category || r.debetor,
            recordLastschrift: r.lastschrift,
            recordFalligkeit: r.falligkeit,
            recordJournaled: r.journaled,
            recordDraft: r.draft,
            recordPriority: r.priority,
            recordDocuments: r.documents,
            recordBankingTransactionId: r.bankingTransactionId,
            recordCashRegisterTransactionId: r.cashRegisterTransactionId,
            recordColor: r.color,
            recordReview: r.review,
            recordContactId: r.contactId,
            recordCountryCode: r.countryCode,
            recordVatNumber: r.vatNumber,
        };
    };

    static convertRecordItemToForm = (i: IGenericItem, product: Product): RecordFormState["editableRecordItem"] => {
        return {
            itemBu: product.productKey() === GQL.IProductKey.ErA ? i.extra.bu : i.bu,
            itemBelegfeld1: i.belegfeld1,
            itemBelegfeld2: i.belegfeld2,
            itemCategoryCreditor: i.category || i.creditor || i.debetor,
            itemBrutto: i.brutto,
            itemOriginalAmount: i.originalAmount,
            itemTag: i.tag,
            itemText: i.text,
            itemText2: i.text2,
            itemUSt13b: i.USt13b,
        };
    };
}
